import { ApolloError } from "@apollo/client";
import LinkIcon from "@mui/icons-material/Link";
import PhotoCameraOutlinedIcon from "@mui/icons-material/PhotoCameraOutlined";
import { Box, Button, Dialog, DialogActions, DialogContent, Typography } from "@mui/material";
import { trackEvent } from "apps/web/src/reporting/reporting";
import { useState } from "react";
import { GptRecipeFragment, useGetPossibleGptRecipeFromImageMutation, useGetPossibleGptRecipeFromUrlMutation } from "../../../../types";
import { GPTLoadingContent } from "../GPTLoadingContent";
import { RecipeGptDialogHeader } from "../RecipeGptDialogHeader";
import { TranscribePresentationMode, TranscribeWithGPTContent } from "./TranscribeWithGPTContent";
import { TranscribeError } from "./utils";

interface TranscribeWithGPTDialogProps {
  open: boolean;
  onClose: () => void;
  onTranscribeComplete: (gptRecipe: GptRecipeFragment, closeModal: () => void) => void;
  onError: (error: any) => void;
  transcriberError: TranscribeError | null;
  setTranscriberError: (error: TranscribeError | null) => void;
  mode: TranscribePresentationMode;
}

const getDialogCopy = (mode: TranscribePresentationMode) => {
  if (mode === "link") {
    return {
      title: "Import from a link",
      subtitle: "Paste a recipe URL and we'll pull in ingredients, steps, and yields as an editable draft.",
      primaryLabel: "Import recipe",
      trustLine: "We only read the recipe markup on the page.",
      loadingText: "Importing",
      icon: <LinkIcon fontSize="small" />,
    };
  }
  return {
    title: "Transcribe a photo",
    subtitle: "Upload a photo of a recipe card, cookbook page, or handwritten note.",
    primaryLabel: "Transcribe photo",
    trustLine: "Photos are used for transcription only.",
    loadingText: "Transcribing",
    icon: <PhotoCameraOutlinedIcon fontSize="small" />,
  };
};

export const TranscribeWithGPTDialog = ({
  open,
  onClose,
  onTranscribeComplete,
  onError,
  transcriberError,
  setTranscriberError,
  mode,
}: TranscribeWithGPTDialogProps) => {
  const [presignedGetImageUrl, setPresignedGetImageUrl] = useState("");
  const [recipeUrl, setRecipeUrl] = useState("");
  const [invalidUrl, setInvalidUrl] = useState(false);

  const clearErrorAndClose = () => {
    setTranscriberError(null);
    onClose();
  };

  const [transcribeImage, { loading: transcribingImage }] = useGetPossibleGptRecipeFromImageMutation({
    onCompleted: data => {
      onTranscribeComplete(data.getPossibleGPTRecipeFromImage, clearErrorAndClose);
    },
  });
  const [transcribeUrl, { loading: transcribingUrl }] = useGetPossibleGptRecipeFromUrlMutation({
    onCompleted: data => {
      onTranscribeComplete(data.getPossibleGPTRecipeFromUrl, clearErrorAndClose);
    },
  });

  const loading = transcribingImage || transcribingUrl;
  const copy = getDialogCopy(mode);

  const handleClose = () => {
    trackEvent("Nutrition | Food Management | Recipe | AI Recipe Transcribe | Cancel Transcription", {});
    if (loading) {
      return;
    }
    clearErrorAndClose();
  };

  const handleTranscribeError = (e: any, inputType: string) => {
    let message = `There was an issue transcribing your ${inputType}.`;
    if (e instanceof ApolloError) {
      if (e.message.includes("invalid image")) {
        // it's possible this is caused by a broken upload link - there should
        // be a log with the full error output from OpenAI
        message = message + " Please try again with a different image. If the issue persists, please reach out to customer support.";
      } else if (e.message.toLowerCase().includes("unreadable image")) {
        message = message + " The image is too blurry to transcribe.";
      } else if (e.message.toLowerCase().includes("no recipe found")) {
        message = message + ` Recipe content could not be found in the provided ${inputType}.`;
      } else if (e.message.toLowerCase().includes("cannot be translated")) {
        message = message + " The transcriber cannot translate non-English recipes at this time.";
      }
    }
    // Clear failed image input so the user can retry; keep URL in place on link
    // failures
    if (inputType === "image") {
      setPresignedGetImageUrl("");
    }
    setTranscriberError(
      new TranscribeError({
        name: "GPT_ERROR",
        message: message,
        cause: e,
      })
    );
  };

  const handleTranscribe = async () => {
    if (mode === "photo" && presignedGetImageUrl) {
      await transcribeImage({ variables: { input: { presignedGetImageUrl } } }).catch(e => handleTranscribeError(e, "image"));
      trackEvent("Nutrition | Food Management | Recipe | AI Recipe Transcribe | Transcribe Image", {});
    } else if (mode === "link" && recipeUrl) {
      await transcribeUrl({ variables: { input: { url: recipeUrl } } }).catch(e => handleTranscribeError(e, "URL"));
      trackEvent("Nutrition | Food Management | Recipe | AI Recipe Transcribe | Transcribe URL", { url: recipeUrl });
    }
  };

  const disablePrimaryButton = loading || (mode === "photo" ? !presignedGetImageUrl : !recipeUrl || invalidUrl);

  const onChangeRecipeUrl = (url: string) => {
    setRecipeUrl(url);
    if (!url) {
      setInvalidUrl(false);
      return;
    }
    try {
      new URL(url);
    } catch {
      setInvalidUrl(true);
      return;
    }
    const urlRegex = /^(((http|https):\/\/|)?[a-z0-9]+([-.]{1}[a-z0-9]+)*\.[a-z]{2,6}(:[0-9]{1,5})?(\/.*)?)$/;
    setInvalidUrl(!urlRegex.test(url));
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <RecipeGptDialogHeader title={copy.title} subtitle={copy.subtitle} icon={copy.icon} onClose={handleClose} />
      <DialogContent sx={{ pt: loading ? 6 : 2, pb: loading ? 6 : 3 }}>
        {loading ? (
          <GPTLoadingContent actionText={copy.loadingText} />
        ) : (
          <TranscribeWithGPTContent
            presignedGetImageUrl={presignedGetImageUrl}
            setPresignedGetImageUrl={setPresignedGetImageUrl}
            recipeUrl={recipeUrl}
            onChangeRecipeUrl={onChangeRecipeUrl}
            invalidUrl={invalidUrl}
            mode={mode}
            transcriberError={transcriberError}
            setTranscriberError={setTranscriberError}
            onError={onError}
          />
        )}
      </DialogContent>
      <DialogActions
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          gap: 2,
          m: 0,
          px: 3,
          pt: 2,
          pb: 3,
        }}
      >
        <Typography variant="caption" color="text.secondary" sx={{ flex: 1, pr: 2 }}>
          {copy.trustLine}
        </Typography>
        <Box sx={{ display: "flex", gap: 1, flexShrink: 0 }}>
          <Button variant="outlined" onClick={handleClose}>
            Cancel
          </Button>
          <span>
            <Button onClick={handleTranscribe} disabled={disablePrimaryButton}>
              {copy.primaryLabel}
            </Button>
          </span>
        </Box>
      </DialogActions>
    </Dialog>
  );
};
